import flatpickr from "flatpickr";
import { German } from "flatpickr/dist/l10n/de.js";
import "select2";

const t = (key, fallback = '') => (window.BL_I18N && BL_I18N[key]) || fallback || key;
const CURRENT_LOCALE = (window.BL_I18N && BL_I18N.locale) || document.documentElement.lang || 'en-US';

const IS_DE = String(CURRENT_LOCALE).toLowerCase().startsWith('de');

class SearchBarWidget {
    constructor({
                    root = ".section-search-form",
                    proxyUrl = "/wp-content/themes/basellodging-theme/api-proxi.php",
                    minStayDays = 1
                } = {}) {
        this.root = root;
        this.proxyUrl = proxyUrl;
        this.minStayDays = minStayDays;

        this.$root = null;
        this.$loc = null;
        this.$gus = null;
        this.$ci  = null;
        this.$co  = null;
        this.$unsure = null;

        this.ciPicker = null;
        this.coPicker = null;

        this.$form = null;
        this.$card = null;
        this.validator = null;

        this.overviewUrl = (window.BL_ROUTES && window.BL_ROUTES.overviewUrl) || null;
    }

    /* ---------- tiny utils ---------- */
    esc(s){return String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#039;")}
    addDays(d,days){const x=new Date(d);x.setDate(x.getDate()+days);return x;}

    buildOptions(list, getValue, getLabel, {placeholder} = {}) {
        let html = placeholder ? `<option value="">${this.esc(placeholder)}</option>` : "";
        html += (list||[]).map(o =>
            `<option value="${this.esc(getValue(o))}">${this.esc(getLabel(o))}</option>`
        ).join("");
        return html;
    }

    normalizeAreaLabel(txt) {
        return String(txt || '').replace(/\s*\(\d+\)\s*$/, '').trim();
    }

    buildOverviewUrl() {
        // base destination
        const base = this.overviewUrl || this.$root.data('overview-url') || '/';

        // values from UI
        const locVal = String(this.$loc.val() || '').trim();

        const areaName = (locVal && locVal !== 'all')
            ? this.normalizeAreaLabel(this.$loc.find('option:selected').text())
            : '';

        const guests = this.$gus.val() || '';
        const dateFrom = this.$ci.val() || '';
        const isUnsureCheckout = this.$unsure.is(':checked');
        const dateTo = isUnsureCheckout ? '' : (this.$co.val() || '');

        const sp = new URLSearchParams();

        // Overview expects AREA NAME, not id
        if (locVal === 'all') sp.set('area', 'all');
        else if (areaName) sp.set('area', areaName);
        if (guests)   sp.set('guests', guests);
        if (dateFrom) sp.set('dateFrom', dateFrom);
        if (dateTo)   { sp.set('dateTo', dateTo); sp.set('availability', 'true'); }
        if (isUnsureCheckout) sp.set('unsureCheckout', 'true');

        const qs = sp.toString();
        return base + (qs ? ('?' + qs) : '');
    }

    registerValidators() {
        // YYYY-MM-DD
        $.validator.addMethod("isoDate", function(value, el) {
            if (!value) return true;
            return /^\d{4}-\d{2}-\d{2}$/.test(value);
        }, t('invalid_date_format','Invalid date format'));

        // dateTo >= dateFrom
        $.validator.addMethod("checkDateOrder", (value, el) => {
            const from = this.$ci.val();
            if (!from || !value) return true;
            return new Date(value) >= new Date(from);
        }, t('checkout_after_checkin','Check-out must be after check-in'));

        // minimum nights
        $.validator.addMethod("minNights", (value, el) => {
            const from = this.$ci.val();
            if (!from || !value) return true;
            const d1 = new Date(from), d2 = new Date(value);
            const diff = Math.round((d2 - d1) / 86400000);
            return diff >= this.minStayDays;
        }, t('min_nights', 'Minimum {n} night(s)').replace('{n}', this.minStayDays));
    }

    initValidation() {
        const SELECTORS = { FORM: this.$form };

        this.validator = $(SELECTORS.FORM).validate({
            focusInvalid: false,
            ignore: [], // validate hidden inputs too (select2 hides the original <select>)
            rules: {
                // dateFrom: optional, but if present must be ISO
                dateFrom: { isoDate: true },

                // dateTo: optional; if present -> ISO + >= dateFrom + min nights
                dateTo:   { isoDate: true, checkDateOrder: true, minNights: true },

                // guests: optional; if present -> digits and >=1
                guests: {
                    digits: {
                        depends: (el) => String($(el).val() || "").trim().length > 0
                    },
                    min: {
                        param: 1,
                        depends: (el) => String($(el).val() || "").trim().length > 0
                    }
                }
            },
            messages: {
                dateFrom: { isoDate: t('invalid_date_format','Invalid date format') },
                dateTo: {
                    isoDate: t('invalid_date_format','Invalid date format'),
                    checkDateOrder: t('checkout_after_checkin','Check-out must be after check-in'),
                    minNights: t('min_nights','Minimum {n} night(s)').replace('{n}', this.minStayDays)
                },
                guests: {
                    digits: t('please_enter_number','Please enter a number'),
                    min: t('at_least_one','Must be at least 1')
                }
            },
            errorPlacement: function(){ return false; },
            highlight: function (el) { $(el).parent().addClass('error'); },
            unhighlight: function (el) { $(el).parent().removeClass('error'); }
        });
    }

    buildGuestsSelectOptions(filtersGuests) {
        // filtersGuests example: [{value: 2}, {value: 4}, {value: 6}]
        let vals = (filtersGuests || [])
            .map(g => Number(g.value))
            .filter(Number.isFinite)
            .filter(n => n > 0);

        const maxShown = vals.length ? Math.max(...vals) : 1;

        const opts = [`<option value="">${t('select_guests','Select guests')}</option>`];

        for (let g = 1; g <= maxShown; g++) {
            opts.push(`<option value="${g}">${g}</option>`);
        }

        return opts.join('');
    }

    /* ---------- API ---------- */
    getAllFilters(){
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${this.proxyUrl}?type=getAllFilters`,
                method: "POST",
                dataType: "json",
                success: (resp) => resolve(resp?.data ?? resp),
                error: (xhr) => reject(xhr?.responseText || "Failed to load filters")
            });
        });
    }

    /* ---------- UI init ---------- */
    fillSelects(filters){
        // location
        const locHtml =
            `<option value="">${this.esc(t('select_location','Select location'))}</option>` +
            `<option value="all">${this.esc(t('select_all','Select all'))}</option>` +
            this.buildOptions(filters?.areas, a => a.id, a => a.name);

        this.$loc.html(locHtml);

        // guests
        const guHtml = this.buildGuestsSelectOptions(filters?.guests);
        this.$gus.html(guHtml);

        // select2 with safe dropdownParent
        const initOne = ($el, placeholder) => {
            if (!$el.length || !$el[0].isConnected) return;

            // destroy previous instance (if any)
            try { if ($el.data('select2')) $el.select2('destroy'); } catch (_) {}

            // use closest .select-box if present and in DOM, else body
            const $box = $el.closest('.select-box');
            const dropdownParent = ($box.length && $box[0].isConnected) ? $box : $(document.body);

            $el.select2({
                minimumResultsForSearch: $el.is(this.$gus) ? Infinity : 0,
                width: '100%',
                dropdownParent,
                dropdownAutoWidth: true,
                placeholder,
                theme: 'basel'
            });
        };

        initOne(this.$loc, t('select_location','Select location'));
        initOne(this.$gus, t('select_guests','Select guests'));
    }

    initDatepickers(){
        const today = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();

        const ensureMobilePlacement = (inst) => {
            if (!window.matchMedia("(max-width: 1023px)").matches) return;
            inst.set("position","below");
            const cal = inst.calendarContainer;
            cal.classList.remove("fp-center");
            requestAnimationFrame(()=> {
                const ch = cal.getBoundingClientRect().height || 0;
                const topAvail = inst._positionElement.getBoundingClientRect().top;
                if (ch + 16 > topAvail) cal.classList.add("fp-center");
            });
        };

        // anchor calendars to input wrappers
        const ciHost = this.$ci.parent().addClass("relative")[0];
        const coHost = this.$co.parent().addClass("relative")[0];

        flatpickr.localize({
            ...(IS_DE ? German : {}),
            firstDayOfWeek: 1
        });

        this.ciPicker = flatpickr(this.$ci[0], {
            allowInput: true,
            dateFormat: "Y-m-d",
            disableMobile: true,
            minDate: today,
            static: true,
            appendTo: ciHost,
            positionElement: this.$ci[0],
            position: "above",
            onOpen: [(_,__,inst)=>{ if(!inst.selectedDates.length) inst.jumpToDate(inst.config.minDate); ensureMobilePlacement(inst);} ],
            onClose: [(_,__,inst)=> inst.calendarContainer.classList.remove("fp-center") ]
        });

        this.coPicker = flatpickr(this.$co[0], {
            allowInput: true,
            dateFormat: "Y-m-d",
            disableMobile: true,
            minDate: today,
            static: true,
            appendTo: coHost,
            positionElement: this.$co[0],
            position: "above",
            onOpen: [(_,__,inst)=>{
                const ci = this.ciPicker.selectedDates[0];
                const minCo = ci ? this.addDays(ci, this.minStayDays) : today;
                this.coPicker.set("minDate", minCo);
                const sel = this.coPicker.selectedDates[0];
                this.coPicker.jumpToDate(sel && sel >= minCo ? sel : minCo);
                ensureMobilePlacement(inst);
            }],
            onClose: [(_,__,inst)=> inst.calendarContainer.classList.remove("fp-center") ]
        });

        // keep check-out >= check-in + minStay
        const reconfigCO = () => {
            const ci = this.ciPicker.selectedDates[0];
            if (ci) {
                const minCo = this.addDays(ci, this.minStayDays);
                this.coPicker.set("minDate", minCo);
                const sel = this.coPicker.selectedDates[0];
                if (sel && sel < minCo) { this.coPicker.clear(); this.$co.trigger("change"); }
                if (this.coPicker.isOpen) this.coPicker.jumpToDate(sel && sel >= minCo ? sel : minCo);
            } else {
                this.coPicker.set("minDate", today);
                if (this.coPicker.isOpen) this.coPicker.jumpToDate(today);
            }
        };
        this.ciPicker.config.onChange.push(()=>{ reconfigCO(); this.$ci.trigger("change"); });

        // "I'm not sure about check-out date"
        const toggleUnsure = () => {
            const checked = this.$unsure.is(":checked");
            if (checked) {
                // clear & disable dateTo, also drop any error highlight
                this.$co.val("");
                if (this.validator) {
                    this.validator.errorsFor(this.$co[0]).remove();
                    this.$co.parent().removeClass('error');
                    this.$co.valid();
                }
                this.$co.prop("disabled", true).addClass("opacity-60 cursor-not-allowed");
            } else {
                this.$co.prop("disabled", false).removeClass("opacity-60 cursor-not-allowed");
                // if check-in exists, revalidate on enable
                if (this.$ci.val()) this.$co.valid();
            }
        };
        this.$unsure.on("change", toggleUnsure);
        toggleUnsure();
    }

    /* ---------- public helpers ---------- */
    getParams(){
        const areaId = this.$loc.val() || null;
        const guests = this.$gus.val() || null;
        const dateFrom = this.$ci.val() || null;
        const dateTo = this.$unsure.is(":checked") ? null : (this.$co.val() || null);

        return { areaId, guests: guests? Number(guests): null, dateFrom, dateTo };
    }

    /* ---------- boot ---------- */
    async boot(){
        this.$root = $(this.root);
        if (!this.$root.length) return;

        this.$loc = this.$root.find('select[data-role="location"]');
        this.$gus = this.$root.find('select[data-role="guests"]');
        this.$ci  = this.$root.find('input[data-role="checkin"]');
        this.$co  = this.$root.find('input[data-role="checkout"]');
        this.$unsure = this.$root.find('input[data-role="unsure-checkout"]');

        try {
            this.$form = this.$root.find("#searchForm");
            this.$card = this.$root.find("#searchFormCard");

            this.registerValidators();
            this.initValidation(); // validator is ready

            const filters = await this.getAllFilters();
            this.fillSelects(filters);
            this.initDatepickers(); // now toggleUnsure is safe

            if (!this.overviewUrl) {
                this.overviewUrl = this.$root.data('overview-url') || '/';
            }

            // submit: validate (format only) and redirect
            this.$form.on('submit', (e) => {
                e.preventDefault();
                if (this.validator && !this.$form.valid()) return;
                const url = this.buildOverviewUrl(); // base or base?params (skips empty)
                window.location.href = url;
            });

            // if the button has no type="submit", still submit the form
            this.$root.find('button[type="submit"], button:not([type])').on('click', (e) => {
                this.$form.trigger('submit');
            });

            requestAnimationFrame(() => {
                this.$card.removeClass("opacity-0");
            });
        } catch (e) {
            console.error("[SearchBarWidget] init error:", e);
        }
    }
}

/* --- mount --- */
const searchBar = new SearchBarWidget({
    root: ".section-search-form",
    proxyUrl: "/wp-content/themes/basellodging-theme/api-proxi.php",
    minStayDays: 1
});
$(document).ready(()=> searchBar.boot());